// MessagePreprocessor.ts
import { safeJsonParse } from "../utils/safeJsonParse";

export interface ChipType {
  label: string;
  action: string;
  tooltip?: string;
  params?: Record<string, any>;
}

export interface TableType {
  headers?: string[]; // Optional headers
  rows: (string | number)[][]; // Array of rows, each row is an array of cells
}

export interface ListType {
  style?: "bulleted" | "numbered";
  items: Array<string | { label: string; value?: string }>;
}

export type FormField =
  | { type: "text" | "textarea" | "email"; name: string; label: string; placeholder?: string; required?: boolean; layout?: "half" | "full"; span?: number; defaultValue?: string }
  | { type: "select"; name: string; label: string; options: string[]; required?: boolean; layout?: "half" | "full"; span?: number; defaultValue?: string }
  | { type: "date" | "time"; name: string; label: string; required?: boolean; layout?: "half" | "full"; span?: number; defaultValue?: string };
// Optional grouped row, e.g. { layout: "row", fields: [ ...FormField ] }
export interface FormRow { layout: "row"; fields: FormField[] }

export interface FormSpec {
  id?: string;
  title?: string;
  submitLabel?: string;
  fields: Array<FormField | FormRow>;
}

export interface CardAction { label: string; action: string; params?: Record<string, any> }
export interface CardSpec {
  title: string;
  subtitle?: string;
  tags?: string[];
  meta?: Record<string, string | number>;
  actions?: CardAction[];
}

export interface Message {
  sender: string;
  content: string;
  chips?: ChipType[];
  pdf_path?: string;
  docx_path?: string;
  recipient_email?: string;
  table?: TableType;
  status?: string;
  suggestions?: string[];
}

interface PreprocessedMessage {
  displayContent: string;
  chips: ChipType[] | undefined;
  table?: TableType;
  list?: ListType;
  form?: FormSpec;
  cards?: CardSpec[];
  pdf_path?: string;
  docx_path?: string;
  recipient_email?: string;
}

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

// Helper to parse Markdown-like tables
const parseMarkdownTable = (text: string): TableType | null => {
  const lines = text.split("\n").map(line => line.trim());
  const tableLines = lines.filter(line => line.startsWith("|") && line.endsWith("|"));
  if (tableLines.length < 2) return null;

  const headers = tableLines[0].slice(1, -1).split("|").map(h => h.trim());
  const rows = tableLines.slice(2).map(line =>
    line.slice(1, -1).split("|").map(cell => cell.trim())
  );
  if (rows.length === 0 || headers.length === 0) return null;

  return { headers, rows };
};

const looksLikeOfferLetterPrompt = (text: string): boolean => {
  const normalized = text.toLowerCase();
  const hasIntent =
    normalized.includes("generate offer letter") ||
    normalized.includes("generate an offer letter") ||
    normalized.includes("create offer letter") ||
    normalized.includes("offer letter");
  const hasCoreFields =
    normalized.includes("candidate") &&
    normalized.includes("designation") &&
    (normalized.includes("ctc") || normalized.includes("cost to company")) &&
    normalized.includes("joining date");
  return hasIntent && hasCoreFields;
};

const buildOfferLetterFallbackForm = (): FormSpec => ({
  title: "Generate Offer Letter",
  submitLabel: "Generate",
  fields: [
    { type: "text", name: "candidate_name", label: "Candidate Name", required: true },
    { type: "text", name: "aadhaar", label: "Aadhaar Number" },
    { type: "text", name: "designation", label: "Designation", required: true },
    { type: "date", name: "joining_date", label: "Joining Date", required: true },
    { type: "text", name: "annual_ctc", label: "Annual CTC (INR)", required: true },
    { type: "text", name: "basic", label: "Basic" },
    { type: "text", name: "hra", label: "HRA" },
    { type: "text", name: "conveyance", label: "Conveyance" },
    { type: "text", name: "special", label: "Special Allowance" },
    { type: "text", name: "bonus", label: "Bonus" },
    { type: "email", name: "recipient_email", label: "Recipient Email", required: true },
  ],
});

const stripOfferLetterChecklist = (text: string): string => {
  const lines = text.split("\n");
  const checklistStart = lines.findIndex((line) =>
    /candidate'?s\s+full\s+name|designation|ctc|cost\s*to\s*company|joining\s+date|basic\s+salary|recipient\s+email/i.test(line)
  );
  if (checklistStart === -1) return text.trim();
  return lines.slice(0, checklistStart).join("\n").trim();
};

const looksLikeScheduleInterviewPrompt = (text: string): boolean => {
  const normalized = text.toLowerCase();
  const hasScheduleIntent =
    normalized.includes("schedule interview") ||
    normalized.includes("schedule an interview") ||
    normalized.includes("interview schedule") ||
    normalized.includes("interview scheduling");

  const hasInterviewContext = normalized.includes("interview");

  const hasRequiredMentions =
    normalized.includes("candidate") &&
    normalized.includes("email") &&
    normalized.includes("date") &&
    normalized.includes("time");

  const asksForDetails =
    normalized.includes("please provide") ||
    normalized.includes("could you please provide") ||
    normalized.includes("i'll need") ||
    normalized.includes("i will need") ||
    normalized.includes("need the candidate") ||
    normalized.includes("desired date") ||
    normalized.includes("these details");

  const looksLikeQuickActionsMenu =
    normalized.includes("how may i help you today") ||
    (normalized.includes("ingest resume") &&
      normalized.includes("search candidates") &&
      normalized.includes("generate offer letter") &&
      normalized.includes("track application"));

  return (hasScheduleIntent || hasInterviewContext) && hasRequiredMentions && asksForDetails && !looksLikeQuickActionsMenu;
};

const buildScheduleInterviewFallbackForm = (): FormSpec => ({
  title: "Schedule Interview",
  submitLabel: "Schedule",
  fields: [
    { type: "text", name: "candidate_name", label: "Candidate Name" },
    { type: "text", name: "candidate_email", label: "Candidate Email" },
    {
      type: "text",
      name: "interviewer_email",
      label: "Interviewer Email",
      defaultValue: "sia.saturniq@gmail.com",
    },
    {
      layout: "row",
      fields: [
        { type: "date", name: "start_date", label: "Start Date", span: 6 },
        { type: "date", name: "end_date", label: "End Date", span: 6 },
      ],
    },
    {
      layout: "row",
      fields: [
        { type: "time", name: "start_time", label: "Start Time", span: 6 },
        { type: "time", name: "end_time", label: "End Time", span: 6 },
      ],
    },
    {
      type: "select",
      name: "mode",
      label: "Mode",
      options: ["Virtual", "Onsite"],
      span: 12,
    },
    {
      type: "select",
      name: "round",
      label: "Interview Round",
      options: ["HR", "Technical", "Manager"],
      span: 12,
    },
  ],
});

export const preprocessMessage = (message: Message): PreprocessedMessage => {
  let displayContent: string = message.content;
  let chips: ChipType[] | undefined = message.chips;
  let table: TableType | undefined = message.table;
  let list: ListType | undefined = undefined;
  let form: FormSpec | undefined = undefined;
  let cards: CardSpec[] | undefined = undefined;
  let pdf_path: string | undefined = message.pdf_path;
  let docx_path: string | undefined = message.docx_path;
  let recipient_email: string | undefined = message.recipient_email;

  let parsedJson: any = null;
  let nonJsonContent: string = message.content.trim();

  // First, try to match a ```json``` block
  const jsonBlockMatch = message.content.match(/```json\s*([\s\S]*?)```/);
  if (jsonBlockMatch) {
    parsedJson = safeJsonParse(jsonBlockMatch[1]);
    nonJsonContent = message.content.replace(jsonBlockMatch[0], "").trim();
  } else {
    // If no ```json``` block, try to find a JSON object at the beginning of the string
    // This regex attempts to find a JSON object that starts with '{' and ends with '}'
    // It's a bit simplistic and might need refinement for complex cases,
    // but should work for the provided example.
    const leadingJsonMatch = message.content.match(/^(\s*\{[\s\S]*?\})\s*([\s\S]*)/);
    if (leadingJsonMatch && isJsonString(leadingJsonMatch[1])) {
      parsedJson = safeJsonParse(leadingJsonMatch[1]);
      nonJsonContent = leadingJsonMatch[2] ? leadingJsonMatch[2].trim() : "";
    } else if (isJsonString(message.content)) {
      // Fallback if the entire content is a JSON string
      parsedJson = safeJsonParse(message.content);
      nonJsonContent = "";
    } else {
      // New: Try to find a JSON object anywhere near the end of the message (e.g., text followed by JSON)
      // We greedily capture from the first '{' that starts a JSON-like block until the last '}' at the end.
      const trailingJsonMatch = message.content.match(/\{[\s\S]*\}\s*$/);
      if (trailingJsonMatch && isJsonString(trailingJsonMatch[0])) {
        parsedJson = safeJsonParse(trailingJsonMatch[0]);
        nonJsonContent = message.content.replace(trailingJsonMatch[0], "").trim();
      }
    }
  }

  if (parsedJson) {
    chips = parsedJson.ui?.chips || parsedJson.chips || chips;
    table = parsedJson.ui?.table || parsedJson.table || table;
    list = parsedJson.ui?.list || parsedJson.list || list;
    form = parsedJson.ui?.form || parsedJson.form || form;
    cards = parsedJson.ui?.cards || parsedJson.cards || cards;

    // Prioritize parsedJson.text for display content
    if (parsedJson.text) {
      displayContent = parsedJson.text;
      if (nonJsonContent) {
        displayContent = `${displayContent}\n\n${nonJsonContent}`;
      }
    } else if (parsedJson.response) {
      displayContent = parsedJson.response;
      if (nonJsonContent) {
        displayContent = `${displayContent}\n\n${nonJsonContent}`;
      }
    } else {
      displayContent = nonJsonContent;
    }

    pdf_path = parsedJson.pdf_path || pdf_path;
    docx_path = parsedJson.docx_path || docx_path;
    recipient_email = parsedJson.recipient_email || recipient_email;
    // If a structured table is present, strip any markdown-like table rows from the display text
    if (table && displayContent) {
      const lines = displayContent.split("\n");
      const filtered = lines.filter(line => !(line.trim().startsWith("|") && line.trim().endsWith("|")));
      displayContent = filtered.join("\n").trim();
    }
  } else {
    // If no JSON was parsed, check for markdown table or use original content
    const markdownTable = parseMarkdownTable(message.content);
    if (markdownTable) {
      table = markdownTable;
      displayContent = message.content
        .split("\n")
        .filter(line => !line.trim().startsWith("|"))
        .join("\n")
        .trim();
    } else {
      displayContent = message.content.trim();
    }
  }

  const fallbackText = displayContent || message.content;

  // Fallback for offer-letter prompts returned as plain text checklists instead of JSON UI payload.
  if (!form && !parsedJson && looksLikeOfferLetterPrompt(fallbackText)) {
    form = buildOfferLetterFallbackForm();
    const cleaned = stripOfferLetterChecklist(fallbackText);
    if (cleaned) {
      displayContent = cleaned;
    }
  }

  // Fallback for interview scheduling prompts returned as plain text instead of JSON UI payload.
  if (!form && !parsedJson && looksLikeScheduleInterviewPrompt(fallbackText)) {
    form = buildScheduleInterviewFallbackForm();
  }

  return { displayContent, chips, table, list, form, cards, pdf_path, docx_path, recipient_email };
};

export const preprocessADKResponse = (adkResponse: any[], sender: string = "Agent"): Message[] => {
  const messages: Message[] = [];

  adkResponse.forEach((item) => {
    const parts = item.content?.parts || [];

    parts.forEach((part: any) => {
      if (part.text) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { displayContent, chips, table, list: _list, form: _form, cards: _cards, pdf_path, docx_path, recipient_email } = preprocessMessage({
          sender,
          content: part.text,
        });
        messages.push({ sender, content: displayContent, chips, table, pdf_path, docx_path, recipient_email });
      }

      if (part.functionResponse) {
        const resp = part.functionResponse.response?.result;
        if (resp) {
          const structured = resp.structuredContent;
          if (structured) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { displayContent, chips, table, list: _list, form: _form, cards: _cards, pdf_path, docx_path, recipient_email } = preprocessMessage({
              sender,
              content: structured.response || structured.text || "",
              chips: structured.ui?.chips || [],
              table: structured.ui?.table || structured.table,
            });
            messages.push({ sender, content: displayContent, chips, table, pdf_path, docx_path, recipient_email });
          } else if (resp.content && Array.isArray(resp.content)) {
            resp.content.forEach((c: any) => {
              if (c.type === "text") {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { displayContent, chips, table, list: _list, form: _form, cards: _cards, pdf_path, docx_path, recipient_email } = preprocessMessage({
                  sender,
                  content: c.text,
                });
                messages.push({ sender, content: displayContent, chips, table, pdf_path, docx_path, recipient_email });
              }
            });
          }
        }
      }

      if (part.functionCall) {
        messages.push({ sender, content: `[Function Call: ${part.functionCall.name}]` });
      }
    });
  });

  return messages;
};
